// Turn CC · 15b — Same-origin API base.
// Frontend + backend are ALWAYS served on the same domain (both preview
// and prod), so relative URLs work everywhere. No env var, no build-time
// baking, no risk of the SPA pointing at an internal cluster hostname.
//
// `API` intentionally starts with `/api` so downstream code stays
// unchanged (`fetch(`${API}/foo`)` → `fetch("/api/foo")`). The browser
// resolves relative URLs against the current origin.
export const API = "/api";

const TOKEN_KEY = "hm_session_token";

export const auth = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (t) => {
    try {
      localStorage.setItem(TOKEN_KEY, t);
    } catch {
      /* ignore */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export function authHeaders(extra = {}) {
  const t = auth.get();
  return t ? { ...extra, Authorization: `Bearer ${t}` } : extra;
}

export async function api(path, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: authHeaders({
      "Content-Type": "application/json",
      ...(options.headers || {}),
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status}: ${text || res.statusText}`);
    err.status = res.status;
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

/**
 * Turn AI — defensive `fetch(...).json()` replacement.
 * Use for any GET/POST that expects a JSON body but where the backend
 * might return a non-JSON payload (500 plain-text, 502 gateway HTML,
 * empty body, etc.). Never throws SyntaxError on malformed JSON.
 *
 * Same contract as fetch() — pass a full URL and options — but returns
 * the parsed JSON directly and raises a real Error with .status set.
 */
export async function apiJson(url, options = {}) {
  const res = await fetch(url, options);
  const ct = res.headers.get("content-type") || "";
  const text = await res.text();
  if (!res.ok) {
    let msg = text.slice(0, 300);
    try {
      const j = JSON.parse(text);
      msg = j.detail || j.message || j.error || msg;
    } catch (_) {
      /* text stays as-is */
    }
    const err = new Error(`HTTP ${res.status}: ${msg}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
  if (!text) return null;
  if (!ct.includes("application/json")) {
    const err = new Error(
      `Expected JSON, got ${ct || "unknown"}: ${text.slice(0, 200)}`,
    );
    err.status = res.status;
    err.body = text;
    throw err;
  }
  try {
    return JSON.parse(text);
  } catch (_) {
    const err = new Error(`Malformed JSON: ${text.slice(0, 200)}`);
    err.status = res.status;
    err.body = text;
    throw err;
  }
}
